/* =============================================================================
   laptop.lk — Product Comparison PDF export  (v2)
   -----------------------------------------------------------------------------
   Adds a "Download PDF" button to the Maximize product-comparison popup and
   renders the compared products as a branded A4 PDF.

   v2 reads each product's own section HTML
   (/products/{handle}?section_id=product-comparison-grid-item) rather than
   scraping the merged popup table. That HTML is already cached by the theme in
   Alpine's store.cacheResults, so in normal use no extra requests are made, and
   the export no longer depends on how snippets/product-comparison-container
   names its cells.

   Standalone: does not modify product-comparison.js or the Alpine store.
   Load AFTER product-comparison.js.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
     1. CONFIG
     ------------------------------------------------------------------------ */
  const CONFIG = {
    storeName: 'laptop.lk',
    tagline: 'Trusted. Genuine. Reliable.',
    website: 'https://laptop.lk',
    contactLine: 'No. 401, Galle Road, Colombo 04, Sri Lanka',
    footnote: 'Specifications and prices are subject to change. Confirm current details on the product page.',

    buttonLabel: 'Download PDF',
    buttonBusyLabel: 'Preparing PDF…',
    filenamePrefix: 'laptop-lk-comparison',

    // Row labels. Change the wording here to match your section block titles.
    labels: {
      price: 'Price',
      rating: 'Rating',
      sku: 'SKU',
      vendor: 'Brand',
      description: 'Description',
      link: 'View online'
    },

    accent: [228, 55, 46],       // #E4372E
    charcoal: [44, 44, 42],      // #2C2C2A
    muted: [120, 120, 116],
    hairline: [226, 226, 222],
    zebra: [250, 250, 248],
    labelFill: [246, 246, 243],

    includeImages: true,
    maxDescriptionChars: 600,
    maxCellChars: 600,
    noReviewsLabel: 'No reviews yet',

    // Loaded only when the button is clicked.
    jsPdfUrl: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
    autoTableUrl: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'
  };

  /* ---------------------------------------------------------------------------
     2. Lazy library loader
     ------------------------------------------------------------------------ */
  let libsPromise = null;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[data-pdf-lib="' + src + '"]')) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.setAttribute('data-pdf-lib', src);
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Could not load the PDF library.')); };
      document.head.appendChild(s);
    });
  }

  function loadLibs() {
    if (!libsPromise) {
      libsPromise = loadScript(CONFIG.jsPdfUrl)
        .then(function () { return loadScript(CONFIG.autoTableUrl); })
        .catch(function (err) { libsPromise = null; throw err; });
    }
    return libsPromise;
  }

  /* ---------------------------------------------------------------------------
     3. Helpers
     ------------------------------------------------------------------------ */
  function getStore() {
    try {
      return (window.Alpine && Alpine.store && Alpine.store('xProductComparison')) || null;
    } catch (e) {
      return null;
    }
  }

  // jsPDF's built-in Helvetica is limited to WinAnsi. Anything above U+00FF is
  // either dropped or mis-mapped — which is why 15.6″ came out as "15.6 3" and
  // dragged the whole cell's letter-spacing with it. Fold the common typographic
  // characters down to ASCII and drop the rest.
  const CHAR_MAP = {
    '\u2033': '"', '\u201C': '"', '\u201D': '"', '\u201E': '"',
    '\u2032': "'", '\u2018': "'", '\u2019': "'", '\u201A': "'",
    '\u2013': '-', '\u2014': '-', '\u2212': '-', '\u2010': '-', '\u2011': '-',
    '\u2026': '...', '\u2022': '-', '\u2043': '-', '\u25CF': '-', '\u25AA': '-',
    '\u2264': '<=', '\u2265': '>=', '\u2260': '!=', '\u2248': '~',
    '\u2192': '->', '\u2190': '<-', '\u200B': '', '\uFEFF': ''
  };

  function toPdfSafe(str) {
    return String(str || '').replace(/[\u0080-\uFFFF]/g, function (ch) {
      if (Object.prototype.hasOwnProperty.call(CHAR_MAP, ch)) return CHAR_MAP[ch];
      // Latin-1 (é, ü, ø, ®, °, ×, ·) is inside WinAnsi and renders correctly.
      return ch.charCodeAt(0) <= 0xFF ? ch : '';
    });
  }

  function clean(el) {
    if (!el) return '';
    // textContent, not innerText: these nodes come from a detached DOMParser
    // document and are never laid out, so innerText returns nothing there.
    const raw = el.textContent || '';
    return toPdfSafe(raw.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim());
  }

  // Rich-text metafields (I/O ports, audio, etc.) are <ul>/<li> or <br> markup.
  // textContent alone welds them into one run: "…480Mbps)1x USB 3.2 Gen 1".
  // Convert block boundaries into real newlines, which autoTable respects.
  function blockText(el) {
    if (!el) return '';
    const owner = el.ownerDocument || document;
    const node = el.cloneNode(true);

    const drop = node.querySelectorAll('script, style, .visually-hidden, .sr-only, .hidden');
    for (let i = 0; i < drop.length; i++) drop[i].parentNode.removeChild(drop[i]);

    const brs = node.querySelectorAll('br');
    for (let i = 0; i < brs.length; i++) {
      brs[i].parentNode.replaceChild(owner.createTextNode('\n'), brs[i]);
    }

    const lis = node.querySelectorAll('li');
    for (let i = 0; i < lis.length; i++) {
      lis[i].insertBefore(owner.createTextNode('\n- '), lis[i].firstChild);
    }

    const blocks = node.querySelectorAll('p, div, tr, h1, h2, h3, h4, h5, h6, li');
    for (let i = 0; i < blocks.length; i++) {
      blocks[i].appendChild(owner.createTextNode('\n'));
    }

    const lines = (node.textContent || '')
      .replace(/\u00a0/g, ' ')
      .split('\n')
      .map(function (line) { return line.replace(/[ \t]+/g, ' ').trim(); })
      .filter(Boolean);

    return toPdfSafe(lines.join('\n'));
  }

  function truncate(str, max) {
    if (!str || str.length <= max) return str || '';
    return str.slice(0, max - 1).replace(/\s+\S*$/, '') + '...';
  }

  function prettify(key) {
    return String(key)
      .split('.').pop()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function pickImageUrl(img) {
    if (!img) return '';
    const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset');
    if (srcset) {
      const parts = srcset.split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean)
        .map(function (s) {
          const bits = s.split(/\s+/);
          return { url: bits[0], w: parseInt(bits[1] || '0', 10) || 0 };
        })
        .sort(function (a, b) { return a.w - b.w; });
      let pick = null;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].w >= 400) { pick = parts[i]; break; }
      }
      if (!pick && parts.length) pick = parts[parts.length - 1];
      if (pick && pick.url) return pick.url;
    }
    const src = img.getAttribute('src') || img.getAttribute('data-src') || '';
    if (src.indexOf('data:') === 0) return '';
    return src;
  }

  function shopifyImageUrl(src, width) {
    if (!src) return '';
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch (e) {
      return src;
    }
  }

  // Re-encode to JPEG so WebP and PNG-with-alpha both land correctly in jsPDF.
  function loadImageAsJpeg(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          const maxW = 560;
          const scale = Math.min(1, maxW / (img.naturalWidth || maxW));
          const c = document.createElement('canvas');
          c.width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
          c.height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
          const ctx = c.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, c.width, c.height);
          ctx.drawImage(img, 0, 0, c.width, c.height);
          resolve({ data: c.toDataURL('image/jpeg', 0.85), w: c.width, h: c.height });
        } catch (e) {
          resolve(null); // tainted canvas — skip the image, keep the PDF
        }
      };
      img.onerror = function () { resolve(null); };
      img.src = shopifyImageUrl(src, 560);
    });
  }

  /* ---------------------------------------------------------------------------
     4. Read the compared products
     ------------------------------------------------------------------------ */

  // Same URL shape the theme uses, so we hit its cache instead of re-fetching.
  function sectionUrl(handle) {
    return '/products/' + handle + '?section_id=product-comparison-grid-item';
  }

  function getProductHtml(store, handle) {
    const url = sectionUrl(handle);
    if (store.cacheResults && store.cacheResults[url]) {
      return Promise.resolve(store.cacheResults[url]);
    }
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('Could not load product details.');
      return res.text();
    }).then(function (text) {
      if (store.cacheResults) store.cacheResults[url] = text;
      return text;
    });
  }

  // The popup table is the source of truth for WHICH rows appear, in WHAT order,
  // and under WHAT label — all three come from the section's blocks. Each body
  // row is: <td class="product-comparison-grid-title">Block title</td> followed
  // by product cells whose inner div carries a class like
  // "product-comparison-sku-{handle}-{index}" or
  // "product-comparison-metafield-{namespace}_{key}-{index}".
  // Strip the trailing "-{index}" and the field type falls out.
  function classifyMarker(el, label) {
    const classes = Array.prototype.slice.call(el.classList);
    for (let i = 0; i < classes.length; i++) {
      const c = classes[i];
      if (c.indexOf('product-comparison-') !== 0) continue;
      const rest = c.slice('product-comparison-'.length);
      const m = rest.match(/^(.*)-(\d+)$/); // greedy: handles hyphens and digits in handles
      if (!m) continue;
      const body = m[1];

      // Order matters: the longer prefixes must be tested first.
      if (body.indexOf('option-swatches-') === 0) {
        // Read the text version even when the popup shows colour chips —
        // swatch cells carry no readable text.
        return { label: label, selector: '.product-comparison-option-' + body.slice(16) };
      }
      if (body.indexOf('option-') === 0) {
        return { label: label, selector: '.product-comparison-option-' + body.slice(7) };
      }
      if (body.indexOf('metafield-') === 0) {
        return { label: label || prettify(body.slice(10)), selector: '.product-comparison-metafield-' + body.slice(10) };
      }
      if (body.indexOf('vendor-new-tab-') === 0) {
        return { label: label || CONFIG.labels.vendor, selector: '.product-comparison-vendor-new-tab' };
      }
      if (body.indexOf('vendor-') === 0) {
        return { label: label || CONFIG.labels.vendor, selector: '.product-comparison-vendor' };
      }
      if (body.indexOf('rating-') === 0) {
        return { label: label || CONFIG.labels.rating, selector: '.product-comparison-rating' };
      }
      if (body.indexOf('sku-') === 0) {
        return { label: label || CONFIG.labels.sku, selector: '.product-comparison-sku' };
      }
      if (body.indexOf('description-') === 0) {
        return { label: label || CONFIG.labels.description, selector: '.product-comparison-description', long: true };
      }
    }
    return null;
  }

  const MARKER_SELECTOR = [
    'rating', 'sku', 'description', 'vendor', 'option', 'metafield'
  ].map(function (k) { return 'div[class*="product-comparison-' + k + '-"]'; }).join(', ');

  function readRowSpecs(store) {
    const table = document.querySelector('.product-comparison-table-popup');
    const specs = [];

    if (table) {
      const bodyRows = table.querySelectorAll('tbody tr');
      for (let i = 0; i < bodyRows.length; i++) {
        const tr = bodyRows[i];
        const marker = tr.querySelector(MARKER_SELECTOR);
        if (!marker) continue; // the "View details" button row has no field
        const titleCell = tr.querySelector('.product-comparison-grid-title');
        const spec = classifyMarker(marker, titleCell ? clean(titleCell) : '');
        if (spec) specs.push(spec);
      }
    }

    if (specs.length) return specs;

    // Fallback: popup never opened, so the x-for cells do not exist yet.
    // Rebuild from the store using CONFIG.labels for wording.
    specs.push({ label: CONFIG.labels.vendor, selector: '.product-comparison-vendor' });
    specs.push({ label: CONFIG.labels.sku, selector: '.product-comparison-sku' });
    specs.push({ label: CONFIG.labels.rating, selector: '.product-comparison-rating' });
    (store.array_variant_options || []).forEach(function (option) {
      specs.push({ label: option, selector: '.product-comparison-option-' + option.split(' ').join('_') });
    });
    (store.array_product_metafields || []).forEach(function (metafield) {
      specs.push({
        label: prettify(metafield),
        selector: '.product-comparison-metafield-' + metafield.split('.').join('_')
      });
    });
    specs.push({ label: CONFIG.labels.description, selector: '.product-comparison-description', long: true });
    return specs;
  }

  async function collectData() {
    const store = getStore();
    if (!store) throw new Error('Comparison is not ready yet. Please try again.');

    const items = (store.listComparisonProducts || []).slice();
    if (!items.length) throw new Error('Add at least one product to the comparison first.');

    const parser = new DOMParser();

    const products = await Promise.all(items.map(async function (item) {
      const handle = item.productHandle;
      const html = await getProductHtml(store, handle);
      const doc = parser.parseFromString(html, 'text/html');

      const header = doc.querySelector('.product-comparison-table-header');

      let titleAnchor = header ? header.querySelector('a.title-link') : null;
      if (!titleAnchor && header) {
        const anchors = Array.prototype.slice.call(header.querySelectorAll('a'));
        for (let i = 0; i < anchors.length; i++) {
          if (clean(anchors[i])) { titleAnchor = anchors[i]; break; }
        }
      }

      let title = clean(titleAnchor);
      if (!title) title = prettify(handle);

      const href = titleAnchor ? titleAnchor.getAttribute('href') : ('/products/' + handle);
      let url = null;
      try { url = new URL(href || ('/products/' + handle), window.location.origin); } catch (e) { url = null; }

      // Price sits in the block right after the title link in the header cell.
      // The Koko widget and the screen-reader "Unit price" label live in the
      // same wrapper, so strip them rather than swallowing the whole run.
      let price = '';
      if (header) {
        const priceEl = header.querySelector('[class*="price"], [id^="Price-"]');
        if (priceEl) {
          const node = priceEl.cloneNode(true);
          const noise = node.querySelectorAll(
            '[class*="koko"], [id*="koko"], [class*="unit-price"], [class*="unit_price"],' +
            ' .visually-hidden, .sr-only, .hidden, script, style, svg, img'
          );
          for (let n = 0; n < noise.length; n++) noise[n].parentNode.removeChild(noise[n]);
          price = clean(node)
            .replace(/\s*or pay in\b[\s\S]*$/i, '')
            .replace(/\bunit price\b/ig, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        }
      }

      return {
        handle: handle,
        doc: doc,
        title: title,
        price: price,
        url: url ? url.href : (CONFIG.website + '/products/' + handle),
        shortUrl: (url ? url.host + url.pathname : 'laptop.lk/products/' + handle).replace(/^www\./, ''),
        image: pickImageUrl(header ? header.querySelector('img') : null)
      };
    }));

    // -- rows -----------------------------------------------------------------
    function buildRow(selector, label, maxChars) {
      const values = products.map(function (p) {
        const el = p.doc.querySelector(selector);
        let text = truncate(blockText(el), maxChars || CONFIG.maxCellChars);
        // Star ratings are SVG, so only the review count survives as text.
        const count = text.match(/^\((\d+)\)$/);
        if (count) {
          const n = parseInt(count[1], 10);
          text = n === 0 ? CONFIG.noReviewsLabel : n + (n === 1 ? ' review' : ' reviews');
        }
        return text;
      });
      if (!values.some(function (v) { return !!v; })) return null;
      return {
        label: label,
        values: values.map(function (v) { return v || '-'; })
      };
    }

    const rows = [];

    (readRowSpecs(store) || []).forEach(function (spec) {
      const row = buildRow(spec.selector, spec.label, spec.long ? CONFIG.maxDescriptionChars : null);
      if (row) rows.push(row);
    });

    products.forEach(function (p) { delete p.doc; });

    return { products: products, rows: rows };
  }

  /* ---------------------------------------------------------------------------
     5. Build the PDF
     ------------------------------------------------------------------------ */
  function formatDate(d) {
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  async function buildPdf() {
    const data = await collectData();
    await loadLibs();

    const jsPDFCtor = window.jspdf && window.jspdf.jsPDF;
    if (!jsPDFCtor) throw new Error('The PDF library did not load. Please try again.');

    const landscape = data.products.length > 2;
    const doc = new jsPDFCtor({
      orientation: landscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 12;
    const generatedOn = formatDate(new Date());

    let images = data.products.map(function () { return null; });
    if (CONFIG.includeImages) {
      images = await Promise.all(data.products.map(function (p) { return loadImageAsJpeg(p.image); }));
    }
    const hasImages = images.some(Boolean);

    // -- body -----------------------------------------------------------------
    const body = [];
    let imageRowIndex = -1;

    if (hasImages) {
      imageRowIndex = body.length;
      body.push([''].concat(data.products.map(function () { return ''; })));
    }

    let priceRowIndex = -1;
    if (data.products.some(function (p) { return !!p.price; })) {
      priceRowIndex = body.length;
      body.push([CONFIG.labels.price].concat(data.products.map(function (p) { return p.price || '-'; })));
    }

    data.rows.forEach(function (row) {
      body.push([row.label].concat(row.values));
    });

    const linkRowIndex = body.length;
    body.push([CONFIG.labels.link].concat(data.products.map(function (p) { return p.shortUrl; })));

    // -- page chrome ----------------------------------------------------------
    function drawHeader() {
      doc.setFillColor(CONFIG.accent[0], CONFIG.accent[1], CONFIG.accent[2]);
      doc.rect(0, 0, pageW, 2.4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(17);
      doc.setTextColor(CONFIG.charcoal[0], CONFIG.charcoal[1], CONFIG.charcoal[2]);
      doc.text(CONFIG.storeName, M, 14);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(CONFIG.muted[0], CONFIG.muted[1], CONFIG.muted[2]);
      doc.text(CONFIG.tagline, M, 19);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(CONFIG.charcoal[0], CONFIG.charcoal[1], CONFIG.charcoal[2]);
      doc.text('Product comparison', pageW - M, 14, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(CONFIG.muted[0], CONFIG.muted[1], CONFIG.muted[2]);
      doc.text(generatedOn, pageW - M, 19, { align: 'right' });

      doc.setDrawColor(CONFIG.hairline[0], CONFIG.hairline[1], CONFIG.hairline[2]);
      doc.setLineWidth(0.2);
      doc.line(M, 23, pageW - M, 23);
    }

    doc.autoTable({
      startY: 29,
      head: [['Specification'].concat(data.products.map(function (p) { return p.title; }))],
      body: body,
      theme: 'grid',
      margin: { left: M, right: M, top: 29, bottom: 16 },
      styles: {
        font: 'helvetica',
        fontSize: 7.8,
        cellPadding: 2.2,
        overflow: 'linebreak',
        valign: 'middle',
        lineColor: CONFIG.hairline,
        lineWidth: 0.15,
        textColor: CONFIG.charcoal
      },
      headStyles: {
        fillColor: CONFIG.accent,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8.2,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2.6
      },
      alternateRowStyles: { fillColor: CONFIG.zebra },
      columnStyles: {
        0: {
          cellWidth: landscape ? 44 : 38,
          fontStyle: 'bold',
          halign: 'left',
          fillColor: CONFIG.labelFill,
          textColor: CONFIG.charcoal
        }
      },
      didParseCell: function (hook) {
        if (hook.section !== 'body') return;
        if (hook.column.index > 0) {
          const raw = hook.cell.raw;
          // Multi-line cells (bullet lists, long specs) read far better
          // left-aligned; short single-line values stay centred.
          hook.cell.styles.halign =
            (typeof raw === 'string' && (raw.indexOf('\n') > -1 || raw.length > 70))
              ? 'left' : 'center';
        }
        if (hook.row.index === imageRowIndex) {
          hook.cell.styles.minCellHeight = 34;
          hook.cell.styles.fillColor = [255, 255, 255];
        }
        if (hook.row.index === priceRowIndex && hook.column.index > 0) {
          hook.cell.styles.fontStyle = 'bold';
          hook.cell.styles.fontSize = 9;
        }
        if (hook.row.index === linkRowIndex && hook.column.index > 0) {
          hook.cell.styles.fontSize = 6.8;
          hook.cell.styles.textColor = CONFIG.muted;
        }
      },
      didDrawCell: function (hook) {
        if (hook.section !== 'body') return;

        if (hook.row.index === imageRowIndex && hook.column.index > 0) {
          const im = images[hook.column.index - 1];
          if (!im) return;
          const maxW = hook.cell.width - 5;
          const maxH = hook.cell.height - 5;
          const ratio = Math.min(maxW / im.w, maxH / im.h);
          const w = im.w * ratio;
          const h = im.h * ratio;
          doc.addImage(
            im.data, 'JPEG',
            hook.cell.x + (hook.cell.width - w) / 2,
            hook.cell.y + (hook.cell.height - h) / 2,
            w, h
          );
        }

        if (hook.row.index === linkRowIndex && hook.column.index > 0) {
          const product = data.products[hook.column.index - 1];
          if (product) {
            doc.link(hook.cell.x, hook.cell.y, hook.cell.width, hook.cell.height, { url: product.url });
          }
        }
      },
      didDrawPage: function () { drawHeader(); }
    });

    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setDrawColor(CONFIG.hairline[0], CONFIG.hairline[1], CONFIG.hairline[2]);
      doc.setLineWidth(0.2);
      doc.line(M, pageH - 12, pageW - M, pageH - 12);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(CONFIG.muted[0], CONFIG.muted[1], CONFIG.muted[2]);
      doc.text(CONFIG.website + '  ·  ' + CONFIG.contactLine, M, pageH - 8);
      doc.text('Page ' + i + ' of ' + total, pageW - M, pageH - 8, { align: 'right' });
      doc.text(CONFIG.footnote, M, pageH - 4.5);
    }

    doc.save(CONFIG.filenamePrefix + '-' + new Date().toISOString().slice(0, 10) + '.pdf');
  }

  /* ---------------------------------------------------------------------------
     6. Button
     ------------------------------------------------------------------------ */
  const ICON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M12 3v11m0 0 4-4m-4 4-4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('is-busy', busy);
    const label = btn.querySelector('.compare-pdf-button__label');
    if (label) label.textContent = busy ? CONFIG.buttonBusyLabel : CONFIG.buttonLabel;
  }

  function showMessage(btn, text) {
    const holder = btn ? btn.parentElement : null;
    if (!holder) { window.alert(text); return; }
    let note = holder.querySelector('.compare-pdf-note');
    if (!note) {
      note = document.createElement('span');
      note.className = 'compare-pdf-note';
      note.setAttribute('role', 'status');
      holder.appendChild(note);
    }
    note.textContent = text;
    clearTimeout(note._t);
    note._t = setTimeout(function () { note.remove(); }, 6000);
  }

  async function download(btn) {
    btn = btn || document.querySelector('.compare-pdf-button');
    setBusy(btn, true);
    try {
      await buildPdf();
    } catch (err) {
      console.error('[compare-pdf]', err);
      showMessage(btn, (err && err.message) || 'The PDF could not be created. Please try again.');
    } finally {
      setBusy(btn, false);
    }
  }

  function makeButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'compare-pdf-button';
    btn.innerHTML = ICON + '<span class="compare-pdf-button__label">' + CONFIG.buttonLabel + '</span>';
    btn.addEventListener('click', function () { download(btn); });
    return btn;
  }

  function injectButton() {
    if (document.querySelector('.compare-pdf-button')) return true;

    // Preferred: <div data-compare-pdf-slot class="compare-pdf-bar"></div>
    // added to sections/product-comparison-overlay.liquid.
    const slot = document.querySelector('[data-compare-pdf-slot]');
    if (slot) {
      slot.appendChild(makeButton());
      return true;
    }

    // Fallback if the slot is missing: above the scrollable table.
    const container = document.querySelector('.product-comparison__table-container');
    if (container && container.parentElement) {
      const bar = document.createElement('div');
      bar.className = 'compare-pdf-bar';
      bar.appendChild(makeButton());
      container.parentElement.insertBefore(bar, container);
      return true;
    }
    return false;
  }

  function start() {
    // The popup lives in an x-teleport <template>, so it only reaches the DOM
    // once Alpine boots — later than DOMContentLoaded.
    if (injectButton()) return;
    const observer = new MutationObserver(function () {
      if (injectButton()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 20000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Public API for a hand-placed Liquid button:
  // <button onclick="window.laptopLkComparisonPDF.download()">Download PDF</button>
  window.laptopLkComparisonPDF = { download: download, config: CONFIG };
})();